"use client";

import React, { useEffect, useRef, useState } from "react";

// ─── Data shapes ─────────────────────────────────────────────────────────────
interface GpuData {
  util_pct: number;
  mem_pct: number;
  mem_used_mb: number;
  mem_total_mb: number;
  name: string;
  temp_c: number;
  power_w: number;
}

interface CpuData {
  cpu_pct: number;
  cpu_cores: number[];
  ram_pct: number;
  ram_used_gb: number;
  ram_total_gb: number;
}

interface QueueStatus {
  queue_depth: number;
  active_requests: number;
  available_servers: number;
  estimated_wait_s: number | null;
  avg_duration_s: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtWait(s: number | null): string {
  if (s === null) return "?";
  if (s < 60) return `~${Math.round(s)}s`;
  return `~${Math.round(s / 60)} min`;
}

function gpuColor(pct: number) {
  if (pct > 85) return "#ef4444";
  if (pct > 50) return "#f59e0b";
  return "#22c55e";
}

function vramColor(pct: number) {
  if (pct > 85) return "#ef4444";
  if (pct > 65) return "#f59e0b";
  return "#60a5fa";
}

function waitColor(pct: number) {
  if (pct > 80) return "#ef4444";
  if (pct > 40) return "#f59e0b";
  return "#94a3b8";
}

// ─── Gauge constants ──────────────────────────────────────────────────────────
// Match the chat bubble: w-14 h-14 = 56px
const SIZE = 56;
const CX = 28;
const CY = 28;

const SW = 3;          // stroke width shared by all rings

const R_GPU  = 24;     // outer ring  — GPU utilisation
const R_VRAM = 17;     // middle ring — VRAM usage
const R_WAIT = 10;     // inner ring  — Ventetid (estimated wait)
const R_BTN  = 5;      // center status dot

// 270° gauge — gap sits at the bottom
const SWEEP_DEG    = 270;
const ROTATION_DEG = 135;

// Pre-computed circumferences and arc lengths
const C_GPU  = 2 * Math.PI * R_GPU;
const ARC_GPU  = C_GPU  * (SWEEP_DEG / 360);
const C_VRAM = 2 * Math.PI * R_VRAM;
const ARC_VRAM = C_VRAM * (SWEEP_DEG / 360);
const C_WAIT = 2 * Math.PI * R_WAIT;
const ARC_WAIT = C_WAIT * (SWEEP_DEG / 360);

function dashOffset(arcLen: number, pct: number): number {
  return arcLen * (1 - Math.max(0, Math.min(100, pct)) / 100);
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function GpuSpeedometer() {
  const [gpu, setGpu]         = useState<GpuData | null>(null);
  const [cpu, setCpu]         = useState<CpuData | null>(null);
  const [queue, setQueue]     = useState<QueueStatus | null>(null);
  const [hovered, setHovered] = useState(false);
  const pollGpuRef   = useRef<() => void>(() => void 0);
  const pollQueueRef = useRef<() => void>(() => void 0);

  useEffect(() => {
    let alive = true;

    const pollGpu = async () => {
      if (!alive) return;
      try {
        const [rGpu, rCpu] = await Promise.all([
          fetch("/api/system/gpu"),
          fetch("/api/system/cpu"),
        ]);
        const dGpu = await rGpu.json();
        const dCpu: CpuData = await rCpu.json();
        if (alive) {
          if (dGpu.available && dGpu.gpus?.length) setGpu(dGpu.gpus[0]);
          setCpu(dCpu);
        }
      } catch { /* silent */ }
    };

    const pollQueue = async () => {
      if (!alive) return;
      try {
        const r = await fetch("/api/queue/status");
        const d = await r.json();
        if (alive) setQueue(d);
      } catch { /* silent */ }
    };

    // Expose fetchers so hover handler can call them
    (pollGpuRef as React.MutableRefObject<() => void>).current = pollGpu;
    (pollQueueRef as React.MutableRefObject<() => void>).current = pollQueue;

    pollGpu();
    pollQueue();
    const id1 = setInterval(pollGpu, 3000);
    const id2 = setInterval(pollQueue, 3000);
    return () => { alive = false; clearInterval(id1); clearInterval(id2); };
  }, []);

  // ── Derived values ──────────────────────────────────────────────────────────
  const gpuPct  = gpu?.util_pct ?? 0;
  const vramPct = gpu?.mem_pct  ?? 0;
  const gc = gpuColor(gpuPct);
  const vc = vramColor(vramPct);

  // Ventetid: normalize estimated_wait_s against avg_duration_s (capped at 60 s scale)
  const waitSec = queue?.estimated_wait_s ?? 0;
  const avgSec  = queue?.avg_duration_s ?? 0;
  const waitPct = avgSec > 0
    ? Math.min((waitSec / Math.max(avgSec * 5, 30)) * 100, 100)
    : Math.min((waitSec / 60) * 100, 100);
  const wc = waitColor(waitPct);

  const offline = queue ? queue.available_servers === 0 : false;
  const busy    = !offline && ((queue?.active_requests ?? 0) > 0 || (queue?.queue_depth ?? 0) > 0);
  const aiColor = offline ? "#ef4444" : busy ? "#3b82f6" : "#22c55e";

  const gpuOff  = dashOffset(ARC_GPU,  gpuPct);
  const vramOff = dashOffset(ARC_VRAM, vramPct);
  const waitOff = dashOffset(ARC_WAIT, waitPct);

  // ── Render ──────────────────────────────────────────────────────────────────
  // Position: same row as chat bubble (bottom-6 = 24px), directly to its left
  // Chat bubble: right-6 (24px) + w-14 (56px) + 8px gap = 88px from right
  return (
    <div
      className="fixed select-none"
      style={{ bottom: 24, right: 88, zIndex: 49 }}
      onMouseEnter={() => { setHovered(true); pollGpuRef.current(); pollQueueRef.current(); }}
      onMouseLeave={() => setHovered(false)}
    >
      {/* ── Speedometer dial ─────────────────────────────────────────────── */}
      <div
        className="relative cursor-pointer transition-transform duration-200"
        style={{
          width: SIZE,
          height: SIZE,
          transform: hovered ? "scale(1.08)" : "scale(1)",
          filter: "drop-shadow(0 3px 12px rgba(0,0,0,0.18))",
        }}
      >
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          aria-label="GPU & AI status monitor"
        >
          {/* White dial background */}
          <circle cx={CX} cy={CY} r={SIZE / 2 - 1} fill="white" stroke="#e5e7eb" strokeWidth="0.75" />

          {/* ── GPU arc (outer) ──────────────────────────────────────────── */}
          <circle cx={CX} cy={CY} r={R_GPU} fill="none" stroke="#f0f0f0" strokeWidth={SW}
            strokeDasharray={`${ARC_GPU} ${C_GPU}`} strokeDashoffset={0} strokeLinecap="round"
            transform={`rotate(${ROTATION_DEG}, ${CX}, ${CY})`} />
          <circle cx={CX} cy={CY} r={R_GPU} fill="none" stroke={gc} strokeWidth={SW}
            strokeDasharray={`${ARC_GPU} ${C_GPU}`} strokeDashoffset={gpuOff} strokeLinecap="round"
            transform={`rotate(${ROTATION_DEG}, ${CX}, ${CY})`}
            style={{ transition: "stroke-dashoffset 0.8s ease, stroke 0.5s ease" }} />

          {/* ── VRAM arc (middle) ────────────────────────────────────────── */}
          <circle cx={CX} cy={CY} r={R_VRAM} fill="none" stroke="#f0f0f0" strokeWidth={SW}
            strokeDasharray={`${ARC_VRAM} ${C_VRAM}`} strokeDashoffset={0} strokeLinecap="round"
            transform={`rotate(${ROTATION_DEG}, ${CX}, ${CY})`} />
          <circle cx={CX} cy={CY} r={R_VRAM} fill="none" stroke={vc} strokeWidth={SW}
            strokeDasharray={`${ARC_VRAM} ${C_VRAM}`} strokeDashoffset={vramOff} strokeLinecap="round"
            transform={`rotate(${ROTATION_DEG}, ${CX}, ${CY})`}
            style={{ transition: "stroke-dashoffset 0.8s ease, stroke 0.5s ease" }} />

          {/* ── Ventetid arc (inner) ─────────────────────────────────────── */}
          <circle cx={CX} cy={CY} r={R_WAIT} fill="none" stroke="#f0f0f0" strokeWidth={SW}
            strokeDasharray={`${ARC_WAIT} ${C_WAIT}`} strokeDashoffset={0} strokeLinecap="round"
            transform={`rotate(${ROTATION_DEG}, ${CX}, ${CY})`} />
          <circle cx={CX} cy={CY} r={R_WAIT} fill="none" stroke={wc} strokeWidth={SW}
            strokeDasharray={`${ARC_WAIT} ${C_WAIT}`} strokeDashoffset={waitOff} strokeLinecap="round"
            transform={`rotate(${ROTATION_DEG}, ${CX}, ${CY})`}
            style={{ transition: "stroke-dashoffset 0.8s ease, stroke 0.5s ease" }} />

          {/* ── Center status dot ────────────────────────────────────────── */}
          <circle cx={CX} cy={CY} r={R_BTN} fill={aiColor} fillOpacity={0.15} />
          <circle cx={CX} cy={CY} r={R_BTN} fill="none" stroke={aiColor} strokeWidth={1} />
          {busy && (
            <circle cx={CX} cy={CY} r={R_BTN + 2} fill="none" stroke={aiColor}
              strokeWidth={0.75} strokeOpacity={0.3} className="animate-ping" />
          )}
        </svg>
      </div>

      {/* ── Hover quick-view popup — appears ABOVE the dial ────────────────── */}
      {hovered && (
        <div
          className="absolute bg-white rounded-xl shadow-2xl border border-gray-200 p-3.5 text-xs text-gray-700 space-y-2.5"
          style={{ bottom: SIZE + 10, right: 0, minWidth: 210 }}
        >
          {/* GPU section */}
          {gpu ? (
            <>
              <div className="flex items-center gap-1.5 pb-2 border-b border-gray-100">
                <span className="text-[10px]">🖥️</span>
                <span className="font-semibold text-gray-800 text-[11px] truncate">{gpu.name}</span>
              </div>

              <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5">
                <span className="text-gray-500 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: gc }} />
                  GPU Load
                </span>
                <span className="font-bold tabular-nums text-right" style={{ color: gc }}>
                  {gpuPct.toFixed(0)}%
                </span>

                <span className="text-gray-500 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: vc }} />
                  VRAM
                </span>
                <span className="font-bold tabular-nums text-right" style={{ color: vc }}>
                  {vramPct.toFixed(0)}%
                  <span className="text-gray-400 font-normal ml-1">
                    {(gpu.mem_used_mb / 1024).toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}/{(gpu.mem_total_mb / 1024).toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} GB
                  </span>
                </span>

                <span className="text-gray-500">🌡️ Temp</span>
                <span className="font-bold tabular-nums text-right">{gpu.temp_c.toFixed(0)}°C</span>

                <span className="text-gray-500">⚡ Effekt</span>
                <span className="font-bold tabular-nums text-right">{gpu.power_w.toFixed(0)} W</span>
              </div>
            </>
          ) : (
            <div className="text-gray-400 text-center py-1 text-[11px]">Ingen GPU data</div>
          )}

          {/* CPU + RAM */}
          {cpu && (
            <>
              <div className="pt-1 border-t border-gray-100" />
              <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5">
                <span className="text-gray-500 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0 bg-orange-400" />
                  CPU
                </span>
                <span className="font-bold tabular-nums text-right text-orange-400">{cpu.cpu_pct.toFixed(0)}%</span>

                {cpu.cpu_cores.map((pct, i) => (
                  <React.Fragment key={i}>
                    <span className="text-gray-400 pl-3 text-[10px]">Core {i}</span>
                    <span className="tabular-nums text-right text-[10px] text-gray-500">{pct.toFixed(0)}%</span>
                  </React.Fragment>
                ))}

                <span className="text-gray-500 flex items-center gap-1.5 pt-1">
                  <span className="w-2 h-2 rounded-full shrink-0 bg-purple-400" />
                  RAM
                </span>
                <span className="font-bold tabular-nums text-right text-purple-400 pt-1">{cpu.ram_pct.toFixed(0)}%</span>

                <span className="text-gray-400 text-[10px]" />
                <span className="text-gray-400 text-[10px] text-right tabular-nums">
                  {cpu.ram_used_gb.toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}/{cpu.ram_total_gb.toLocaleString("da-DK", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} GB
                </span>
              </div>
            </>
          )}

          {/* AI queue section */}
          {queue && (
            <>
              <div className="pt-1 border-t border-gray-100 flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: aiColor }} />
                <span className="font-semibold text-gray-800 text-[11px]">
                  {offline ? "AI Offline" : "AI Kø-status"}
                </span>
              </div>

              <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5">
                <span className="text-gray-500">Aktive</span>
                <span className="font-bold text-right">{queue.active_requests}</span>

                <span className="text-gray-500">I kø</span>
                <span className="font-bold text-right">{queue.queue_depth}</span>

                <span className="text-gray-500 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: wc }} />
                  Ventetid
                </span>
                <span className="font-bold tabular-nums text-right" style={{ color: wc }}>
                  {fmtWait(queue.estimated_wait_s)}
                </span>

                <span className="text-gray-500">Gns. svartid</span>
                <span className="font-bold text-right">{fmtWait(queue.avg_duration_s)}</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
